import React, { useState, useEffect } from 'react';
import { PageHeader } from '../design';
import { useParams, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  User,
  ArrowLeft,
  RefreshCw,
  Building2,
  Shield,
  Activity,
  DollarSign,
  Mail,
  Calendar,
  FileCheck,
  Clock,
  CheckCircle,
  Zap,
  UserCheck,
  Key,
  AtSign,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';

export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (isAuthenticated && userId) {
      fetchUserDetail();
    }
  }, [isAuthenticated, userId]);

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getUserDetail(userId);
      setUserData(res.data);
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <User className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">User Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested user could not be loaded.</p>
        <Button onClick={() => navigate('/governance/users')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
      </div>
    );
  }

  const user = userData.user;

  return (
    <div className="space-y-6" data-testid="user-detail">
      {/* Header */}
      <PageHeader
        breadcrumb={
          <button onClick={() => navigate('/governance/users')} className="inline-flex items-center gap-1.5 hover:gov-text-primary transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Users
          </button>
        }
        title={user.name || 'Unknown User'}
        subtitle={user.email}
        actions={
          <>
            <Badge
              variant={user.status === 'active' ? 'default' : 'secondary'}
              className={`text-sm px-3 py-1 ${user.status === 'active' ? 'bg-green-600' : ''}`}
            >
              {user.status === 'active' ? (
                <><CheckCircle className="h-4 w-4 mr-1" /> ACTIVE</>
              ) : (
                <><Clock className="h-4 w-4 mr-1" /> {user.status?.toUpperCase() || 'INACTIVE'}</>
              )}
            </Badge>
            <Badge variant="outline" className="text-sm px-3 py-1 capitalize">
              {user.role || 'user'}
            </Badge>
            <Button variant="outline" size="icon" onClick={fetchUserDetail}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </>
        }
      />

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-6">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-xl font-bold">{userData.ai_activity?.executions_30d || 0}</div>
                <p className="text-xs text-muted-foreground">AI Executions (30d)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-orange-500" />
              <div>
                <div className="text-xl font-bold">{userData.ai_activity?.approvals_requested || 0}</div>
                <p className="text-xs text-muted-foreground">Approvals Requested</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-xl font-bold truncate max-w-[100px]" title={userData.tenant?.name}>
                  {userData.tenant?.name || 'N/A'}
                </div>
                <p className="text-xs text-muted-foreground">Tenant</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-slate-500" />
              <div>
                <div className="text-xl font-bold truncate max-w-[100px]" title={userData.organization?.name}>
                  {userData.organization?.name || 'N/A'}
                </div>
                <p className="text-xs text-muted-foreground">Organization</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-xl font-bold capitalize">{userData.subscription?.plan_id || 'Free'}</div>
                <p className="text-xs text-muted-foreground">Plan</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-teal-500" />
              <div>
                <div className="text-xl font-bold capitalize">{user.role || 'user'}</div>
                <p className="text-xs text-muted-foreground">Role</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* User Details Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Details
            </CardTitle>
            <CardDescription>Profile and account information</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">User ID</div>
                <div className="font-mono text-sm break-all select-all">{userId}</div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <AtSign className="h-4 w-4" /> Email
                </span>
                <span className="font-medium">{user.email}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <User className="h-4 w-4" /> Name
                </span>
                <span>{user.name || 'N/A'}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Key className="h-4 w-4" /> Role
                </span>
                <Badge variant="outline" className="capitalize">{user.role || 'user'}</Badge>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" /> Status
                </span>
                <Badge 
                  variant={user.status === 'active' ? 'default' : 'secondary'}
                  className={user.status === 'active' ? 'bg-green-600' : ''}
                >
                  {user.status || 'unknown'}
                </Badge>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email Verified
                </span>
                <Badge variant={user.email_verified ? 'default' : 'secondary'}>
                  {user.email_verified ? 'Verified' : 'Not Verified'}
                </Badge>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4" /> Created
                </span>
                <span>{formatDate(user.created_at)}</span>
              </div>
              
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Last Login
                </span>
                <span className={!user.last_login ? 'text-muted-foreground' : ''}>
                  {user.last_login ? formatDate(user.last_login) : 'Never'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization & Tenant Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Organization & Tenant
            </CardTitle>
            <CardDescription>Workspace assignment and subscription</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Organization Section */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-5 w-5 text-slate-500" />
                  <span className="font-medium">Organization</span>
                </div>
                <div className="pl-7 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Name</span>
                    <span>{userData.organization?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">ID</span>
                    <span className="font-mono text-xs">{userData.organization?.id || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {/* Tenant Section */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <span className="font-medium">Tenant</span>
                </div>
                <div className="pl-7 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Name</span>
                    <span>{userData.tenant?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">ID</span>
                    <span className="font-mono text-xs">{userData.tenant?.id || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {/* Subscription Section */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5 text-green-500" />
                  <span className="font-medium">Subscription</span>
                </div>
                <div className="pl-7 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Plan</span>
                    <Badge 
                      variant="default"
                      className={`capitalize ${
                        userData.subscription?.plan_id === 'enterprise' ? 'bg-purple-600' :
                        userData.subscription?.plan_id === 'pro' ? 'bg-blue-600' :
                        userData.subscription?.plan_id === 'starter' ? 'bg-green-600' : ''
                      }`}
                    >
                      {userData.subscription?.plan_id || 'Free'}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Status</span>
                    <Badge 
                      variant={userData.subscription?.billing_status === 'active' ? 'default' : 'destructive'}
                      className={userData.subscription?.billing_status === 'active' ? 'bg-green-600' : ''}
                    >
                      {userData.subscription?.billing_status || 'Active'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent AI Activity
            </CardTitle>
            <CardDescription>Last 10 AI executions by this user</CardDescription>
          </CardHeader>
          <CardContent>
            {userData.recent_activity?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No AI activity recorded for this user
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Agent Type</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Timestamp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userData.recent_activity?.map((activity, i) => (
                    <TableRow key={activity.id || i}>
                      <TableCell>
                        <span className="font-medium">{activity.action || activity.event_type || 'AI Execution'}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {activity.agent_type || 'unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {activity.entity_type || 'N/A'} 
                          {activity.entity_id ? ` (${activity.entity_id.substring(0, 8)}...)` : ''}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={activity.status === 'completed' ? 'default' : 'secondary'}>
                          {activity.status || 'completed'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {formatDate(activity.timestamp)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
